import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import classnames from "classnames";
import { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Autocomplete, MenuItem, TextField, } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { FontAwesomeIcon } from "./FontAwesomeIcon";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import "./DoraSelect.scss";
import { useElementId } from "./useElementId";
export const DoraChevronIcon = (_jsx(FontAwesomeIcon, { icon: faAngleDown, style: { fontSize: "0.875rem", color: "var(--gray-400)" } }));
const setInputWidth = (text, hasIcon) => {
    return 65 + text.length * 9 + (hasIcon ? 20 : 0);
};
const DoraSelect = (props) => {
    const { autofocus, openOnFocus, autoselect, id, icon, options, nullable, selectableOptions, placeholder, fullWidth, className, freeSolo, clearable, size, disabled, controlField, onInputChange, onChange, error, value, customRenderOption, customRenderInput, customGetOptionLabel, label, sx, InputProps, skipMinWidth, maxWidth, ...rest } = props;
    const { t } = useTranslation("components");
    const [length, setLength] = useState(placeholder ? setInputWidth(placeholder) : 120);
    const wrapperClasses = classnames("dora-select__wrapper", disabled ? "dora-select__wrapper--disabled" : null, error ? "dora-select__wrapper--error" : null, className);
    const staticId = useElementId();
    const customId = id || staticId;
    const onOptionSelected = (v) => {
        if (onChange) {
            onChange(v?.value);
        }
        setLength(setInputWidth(v?.label || "", !!icon));
    };
    const onAdornmentClick = () => {
        const input = document.getElementById(customId);
        input?.parentElement?.click();
    };
    const valueProp = controlField
        ? {
            value: options.find((option) => option.value === controlField.value) || null,
        }
        : {
            value: options.find((option) => option.value === value) || null,
        };
    const defaultGetOptionLabel = (option) => option?.label || "";
    const defaultRenderOption = (props, option) => {
        return (_createElement(MenuItem, { ...props, key: option.value ?? "", disabled: option.unselectable }, option.label));
    };
    const defaultRenderInput = (params) => (_jsx(TextField, { ...params, id: customId, placeholder: placeholder, label: label, autoFocus: autofocus, ...(label && { InputLabelProps: { shrink: true } }), sx: {
            ...(fullWidth
                ? {}
                : skipMinWidth
                    ? {}
                    : {
                        width: `${length}px !important`,
                        minWidth: maxWidth ? `${Math.min(180, maxWidth)}px` : `180px`,
                    }),
            ...(maxWidth && { maxWidth: `${maxWidth}px !important` }),
            ...sx,
        }, inputProps: {
            ...params.inputProps,
            ...rest,
        }, InputProps: {
            ...params.InputProps,
            sx: {
                "& .MuiInputBase-input": {
                    textIndent: icon ? 20 : 0,
                    textOverflow: "ellipsis",
                },
            },
            startAdornment: icon ? (_jsx("div", { className: "dora-select__icon", onClick: onAdornmentClick, children: _jsx(FontAwesomeIcon, { icon: icon }) })) : null,
            ...InputProps,
        } }));
    const optionsWithEmptyOption = useMemo(() => {
        const allViewableOptions = selectableOptions || options;
        return allViewableOptions.length
            ? allViewableOptions
            : [{ value: "", label: t("optionLabels.noOptions"), unselectable: true }];
    }, [options, selectableOptions, t]);
    return (_jsx(FormControl, { size: size || "small", className: wrapperClasses, fullWidth: fullWidth, sx: maxWidth ? { maxWidth: `${maxWidth}px` } : undefined, children: _jsx(Autocomplete, { style: props.style || {}, size: size || "small", fullWidth: true, disableClearable: !clearable, autoSelect: autoselect, openOnFocus: openOnFocus, autoHighlight: true, forcePopupIcon: true, popupIcon: DoraChevronIcon, slotProps: {
                popper: { sx: { minWidth: "fit-content" } },
                clearIndicator: { sx: { color: "var(--gray-400)" } },
                popupIndicator: {
                    sx: {
                        color: "var(--gray-400)",
                        fontSize: "1rem",
                        padding: "0.5rem",
                    },
                },
            }, sx: {
                margin: 0,
                background: disabled ? "var(--gray-100)" : "white",
                "& input::placeholder": {
                    color: "var(--gray-800)",
                    fontWeight: 400,
                },
                "& fieldset": { borderColor: "var(--gray-300)" },
                "& .MuiOutlinedInput-root:not(.Mui-focused):hover fieldset": {
                    borderColor: "var(--gray-400) !important",
                },
                ...(!disabled && { "&:hover": { borderColor: "var(--gray-300)" } }),
                "& .MuiFormControl-root.MuiFormControl-marginDense.MuiFormControl-fullWidth.MuiTextField-root": {
                    margin: "0 !important",
                    ...(fullWidth && { width: "100% !important" }),
                },
                "& .MuiFormControl-root": fullWidth
                    ? { width: "100% !important" }
                    : {},
                "& .MuiAutocomplete-endAdornment": {
                    right: "0.25rem !important",
                },
            }, options: optionsWithEmptyOption, getOptionLabel: customGetOptionLabel || defaultGetOptionLabel, renderOption: customRenderOption || defaultRenderOption, renderInput: customRenderInput || defaultRenderInput, disabled: disabled, id: customId, ...(freeSolo && { freeSolo }), ...controlField, ...valueProp, ...(onInputChange && { onInputChange }), onChange: controlField
                ? (_, v) => {
                    controlField.onChange(nullable ? v.value : v.value || "");
                    onOptionSelected(v);
                }
                : (_, v) => onOptionSelected(v) }) }));
};
const UFDoraSelect = (props) => {
    const { control, name, ...rest } = props;
    return (_jsx(Controller, { name: name, control: control, render: ({ field }) => _jsx(DoraSelect, { controlField: field, ...rest }) }));
};
export { DoraSelect, UFDoraSelect };
//# sourceMappingURL=DoraSelect.js.map